import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { creatorScoreAPIClient } from "@/marketing/infrastructure/influencer/campaign/score/creatorScoreAPIClient";
import type { CreatorScoreSummary } from "@/marketing/models/creatorScore";
import type { CreatorScoreIdentity } from "@/marketing/models/creatorScore";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from "@/marketing/components/ui/sheet";
import { CreatorScoreDetail } from "./CreatorScoreDetail";

interface ScoreContext {
  scores: Record<string, CreatorScoreSummary>;
  isLoading: boolean;
  isError: boolean;
  openScore: (creator: CreatorScoreIdentity) => void;
}
const Context = createContext<ScoreContext>({
  scores: {},
  isLoading: false,
  isError: true,
  openScore: () => undefined,
});
export function useCreatorScores() {
  return useContext(Context);
}
interface CreatorScoreProviderProps {
  campaignId: string;
  creators: CreatorScoreIdentity[];
  children: ReactNode;
  enabled?: boolean;
}
export function CreatorScoreProvider({
  campaignId,
  creators,
  children,
  enabled = true,
}: CreatorScoreProviderProps) {
  const client = useQueryClient();
  const [selected, setSelected] = useState<CreatorScoreIdentity | null>(null);
  useEffect(() => {
    if (!enabled) {
      void client.cancelQueries({ queryKey: ["creatorScores", campaignId] });
      setSelected(null);
    }
  }, [enabled, campaignId, client]);
  const linkedUserIds = [...new Set(creators.flatMap((creator) =>
    creator.userId == null ? [] : [creator.userId],
  ))].sort((first, second) => first - second);
  const query = useQuery({
    enabled,
    queryKey: ["creatorScores", campaignId, linkedUserIds],
    queryFn: ({ signal }) => creatorScoreAPIClient.listCampaignScores(campaignId, signal),
    staleTime: 0,
    refetchOnMount: "always",
    retry: false,
    throwOnError: false,
  });
  return (
    <Context.Provider
      value={{
        scores: query.data?.scores ?? {},
        isLoading: query.isLoading,
        isError: query.isError,
        openScore: setSelected,
      }}
    >
      {children}
      <Sheet
        open={enabled && selected !== null}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      >
        <SheetContent className="tw-w-full tw-overflow-y-auto tw-p-6 sm:tw-max-w-[640px]">
          <SheetTitle>
            Creator Score ·{" "}
            {selected?.handle ? `@${selected.handle}` : "Details"}
          </SheetTitle>
          <SheetDescription>
            Global user score out of 10. Campaign requirements are evaluated
            separately.
          </SheetDescription>
          {enabled && selected && (
            <CreatorScoreDetail
              key={selected.influencerId}
              campaignId={campaignId}
              creator={selected}
            />
          )}
        </SheetContent>
      </Sheet>
    </Context.Provider>
  );
}
